'use client'

import { Rocket, Sparkles, X } from 'lucide-react'
import { usePathname, useRouter } from 'next/navigation'
import { useState } from 'react'
import { useJson } from '@/components/dashboard/credits/blocks/useJson'
import { useIsOwner } from '@/hooks/useIsOwner'
import { cn } from '@/lib/utils'

type OnboardingRuntimeRes = {
  ok: boolean
  onboarding?: {
    enabled: boolean
    completedCount: number
    totalCount: number
    progressPercent: number
    currentPendingStep?: {
      id: number
      title: string
      description?: string | null
    } | null
  }
}

type Props = {
  sellerId?: number | null
  visible?: boolean
  className?: string
  variant?: 'default' | 'floating' | 'launcher'
}

export function OnboardingProgressBanner({
  sellerId,
  visible = true,
  className,
  variant = 'default',
}: Props) {
  const router = useRouter()
  const pathname = usePathname()
  const [dismissed, setDismissed] = useState(false)
  const { isOwner } = useIsOwner()

  const s = useJson<OnboardingRuntimeRes>(
    sellerId ? `/api/dashboard/onboarding/runtime?sellerId=${sellerId}` : ''
  )

  if (!visible) return null
  if (!sellerId) return null
  if (dismissed) return null
  if (isOwner) return null
  if (pathname === '/dashboard') return null
  if (pathname === '/creator-studio') return null
  if (pathname === '/funnel-builder') return null
  if (s.status !== 'ok') return null

  const onboarding = s.data?.onboarding

  if (!onboarding?.enabled) return null
  if (onboarding.totalCount <= 0) return null
  if (onboarding.progressPercent >= 100) return null

  const pendingTitle =
    onboarding.currentPendingStep?.title || 'Continúa tu configuración'

  if (variant === 'launcher') {
    return (
      <div className={cn('rounded-[2rem] hubee-premium-card', className)}>
        <div className="hubee-premium-content flex min-h-[138px] flex-col gap-5 p-5 md:flex-row md:items-center md:gap-6 md:p-6">
          <div className="relative flex h-[88px] w-[88px] shrink-0 items-center justify-center">
            <svg className="h-full w-full -rotate-90" viewBox="0 0 100 100">
              <circle
                cx="50"
                cy="50"
                r="39"
                fill="none"
                stroke="hsl(var(--muted))"
                strokeWidth="8"
              />
              <circle
                cx="50"
                cy="50"
                r="39"
                fill="none"
                stroke="hsl(var(--primary))"
                strokeWidth="8"
                strokeLinecap="round"
                strokeDasharray={`${2 * Math.PI * 39}`}
                strokeDashoffset={`${
                  2 * Math.PI * 39 -
                  (2 * Math.PI * 39 * onboarding.progressPercent) / 100
                }`}
                className="transition-all duration-700"
              />
            </svg>

            <span className="absolute text-[18px] font-medium text-primary">
              {onboarding.progressPercent}%
            </span>
          </div>

          <div className="min-w-0 flex-1">
            <div className="mb-2 flex flex-wrap items-center gap-2">
              <p className="text-[15px] font-medium tracking-[-0.015em] text-foreground/90">
                Tu siguiente mejor acción
              </p>

              <span className="rounded-full bg-background/60 px-3 py-1 text-xs font-medium text-muted-foreground ring-1 ring-border/35">
                Paso 1 de 4
              </span>
            </div>

            <h2 className="text-[21px] font-normal leading-snug tracking-[-0.025em] text-foreground/90 md:text-[24px]">
              {pendingTitle}
            </h2>
          </div>

          <div className="flex w-full shrink-0 flex-col gap-3 md:w-[250px]">
            <button
              type="button"
              onClick={() => router.push('/dashboard')}
              className="inline-flex h-11 items-center justify-center gap-2 rounded-[1rem] bg-primary px-5 text-sm font-medium text-primary-foreground shadow-[0_16px_40px_hsl(var(--primary)/0.26)] transition hover:opacity-90"
            >
              Continuar con mi ruta de activación
              <Rocket className="h-4 w-4" />
            </button>

            <button
              type="button"
              onClick={() => router.push('/setup')}
              className="hidden inline-flex h-10 items-center justify-center rounded-[1rem] border border-border/35 bg-background/45 px-5 text-sm font-medium text-muted-foreground backdrop-blur-xl transition hover:bg-background/65 hover:text-foreground"
            >
              Ver ruta completa
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div
      className={[
        variant === 'floating' ? '' : 'px-4 pt-4 md:px-6',
        className || '',
      ].join(' ')}
    >
      <div
        className={[
          'relative overflow-hidden rounded-[1.5rem]',
          'border border-border/60 bg-background/88',
          'shadow-[0_18px_60px_hsl(var(--foreground)/0.08)]',
          'ring-1 ring-border/40 backdrop-blur-2xl',
        ].join(' ')}
      >
        <div className="relative px-4 py-3">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-primary/10 text-primary ring-1 ring-primary/15">
              <Sparkles className="h-4 w-4" />
            </div>

            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span className="truncate text-sm font-semibold text-foreground">
                  Continúa tu onboarding
                </span>

                <span className="shrink-0 rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-bold text-primary">
                  {onboarding.progressPercent}%
                </span>
              </div>

              <div className="mt-1 flex min-w-0 items-center gap-2">
                <span className="truncate text-xs font-medium text-muted-foreground">
                  {pendingTitle}
                </span>

                <div className="h-1.5 w-20 shrink-0 overflow-hidden rounded-full bg-muted">
                  <div
                    className="h-full rounded-full bg-primary transition-all duration-700"
                    style={{ width: `${onboarding.progressPercent}%` }}
                  />
                </div>
              </div>
            </div>

            <button
              type="button"
              onClick={() => router.push('/dashboard')}
              className="hidden h-9 shrink-0 items-center justify-center gap-2 rounded-full bg-primary px-4 text-[11px] font-bold uppercase tracking-[0.14em] text-primary-foreground transition hover:opacity-90 sm:inline-flex"
            >
              <Rocket className="h-3.5 w-3.5" />
              Continuar
            </button>

            <button
              type="button"
              onClick={() => setDismissed(true)}
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-muted/70 text-muted-foreground transition hover:bg-muted hover:text-foreground"
              aria-label="Cerrar onboarding"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <button
            type="button"
            onClick={() => router.push('/dashboard')}
            className="mt-3 inline-flex h-9 w-full items-center justify-center gap-2 rounded-full bg-primary text-[11px] font-bold uppercase tracking-[0.14em] text-primary-foreground transition hover:opacity-90 sm:hidden"
          >
            <Rocket className="h-3.5 w-3.5" />
            Continuar
          </button>
        </div>
      </div>
    </div>
  )
}